import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

// Custom tooltip to show engagement metrics
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipLabel>{label} Dimension</TooltipLabel>
        {payload.map((entry, index) => (
          <TooltipValue key={index} style={{ color: entry.color }}>
            <strong>{entry.dataKey}:</strong> {entry.value}%
          </TooltipValue>
        ))}
        <TooltipNote>
          Scores are relative to other users in this session
        </TooltipNote>
      </TooltipContainer>
    );
  }
  return null;
}

// Custom legend renderer
const renderLegend = (props) => {
  const { payload } = props;
  return (
    <div style={{ textAlign: 'center' }}>
      {payload.map((entry, index) => (
        <LegendText
          key={`legend-${index}`}
          style={{
            color: entry.color,
            display: 'inline-block',
            margin: '0 8px'
          }}>
          {entry.value}
        </LegendText>
      ))}
    </div>
  );
};

// Custom tick component for radar chart labels with position-specific styling
function CustomRadarTick({ payload, x, y, textAnchor }) {
  if (!payload || !payload.value) return null;

  const metricIndex = payload.index || 0;
  const totalMetrics = 6;

  // Calculate positioning based on angular position (starting from top, going clockwise)
  const angle = (metricIndex * 360) / totalMetrics;
  let customTextAnchor = textAnchor;
  let dy = 0;

  // Adjust positioning based on angle
  if (angle >= 330 || angle <= 30) {
    // Top area (Quality)
    customTextAnchor = 'middle';
    dy = -4;
  } else if (angle > 30 && angle <= 90) {
    // Top-right area (Frequency)
    customTextAnchor = 'start';
    dy = -1;
  } else if (angle > 90 && angle <= 150) {
    // Bottom-right area (Coverage)
    customTextAnchor = 'start';
    dy = 8;
  } else if (angle > 150 && angle <= 210) {
    // Bottom area (Duration)
    customTextAnchor = 'middle';
    dy = 8;
  } else if (angle > 210 && angle <= 270) {
    // Bottom-left area (Community)
    customTextAnchor = 'end';
    dy = 8;
  } else if (angle > 270 && angle < 330) {
    // Top-left area (Consistency)
    customTextAnchor = 'end';
    dy = -1;
  }

  return (
    <text
      x={x}
      y={y}
      dy={dy}
      textAnchor={customTextAnchor}
      fontSize={12}
      fill="var(--brown)"
      fontFamily="Inter">
      {payload.value}
    </text>
  );
}

function EngagementQualityRadarChart({ users }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Initialize selectedUsers with top 3 users on first render
  useEffect(() => {
    if (users && users.length > 0 && selectedUsers.length === 0) {
      const topUsers = [...users]
        .sort((a, b) => (b.engagementScore || 0) - (a.engagementScore || 0))
        .slice(0, 3);
      setSelectedUsers(topUsers);
    }
  }, [users, selectedUsers.length]);

  // Show empty state if no users
  if (!users || !Array.isArray(users) || users.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>No user engagement data available</EmptyMessage>
      </EmptyState>
    );
  }

  // Process users to create engagement profiles
  const processUserEngagementProfile = (user) => {
    // Calculate max values for normalization
    const maxComments = Math.max(...users.map((u) => u.comments || 0), 1);
    const maxSessionTime = Math.max(...users.map((u) => u.sessionTime || 0), 1);
    const maxReplies = Math.max(...users.map((u) => u.replies || 0), 1);
    const maxQuestionsEngaged = Math.max(
      ...users.map((u) => Object.keys(u.questionEngagement || {}).length),
      1
    );

    // Calculate engagement metrics (normalized to 0-100)
    const commentQuality = user.contentQuality
      ? Math.min(
          100,
          (user.contentQuality.qualityScore /
            Math.max(
              ...users.map((u) => u.contentQuality?.qualityScore || 0),
              1
            )) *
            100
        )
      : 0;
    const participationFrequency = Math.min(
      100,
      ((user.comments + user.replies) / (maxComments + maxReplies)) * 100
    );
    const questionCoverage = Math.min(
      100,
      (Object.keys(user.questionEngagement || {}).length /
        maxQuestionsEngaged) *
        100
    );
    const sessionDuration = Math.min(
      100,
      (user.sessionTime / maxSessionTime) * 100
    );
    const communityEngagement =
      user.comments > 0
        ? Math.min(100, (user.replies / user.comments) * 100)
        : 0;
    const consistency = user.engagementScore || 0; // Use existing engagement score as consistency metric

    return [
      {
        metric: 'Quality',
        [user.name]: Math.round(commentQuality),
        fullMark: 100
      },
      {
        metric: 'Frequency',
        [user.name]: Math.round(participationFrequency),
        fullMark: 100
      },
      {
        metric: 'Coverage',
        [user.name]: Math.round(questionCoverage),
        fullMark: 100
      },
      {
        metric: 'Duration',
        [user.name]: Math.round(sessionDuration),
        fullMark: 100
      },
      {
        metric: 'Community',
        [user.name]: Math.round(communityEngagement),
        fullMark: 100
      },
      {
        metric: 'Consistency',
        [user.name]: Math.round(consistency),
        fullMark: 100
      }
    ];
  };

  // Get top 5 users by engagement score for initial display
  const topUsers = [...users]
    .sort((a, b) => (b.engagementScore || 0) - (a.engagementScore || 0))
    .slice(0, 5);

  // Create combined radar data
  const radarData = [
    { metric: 'Quality', fullMark: 100 },
    { metric: 'Frequency', fullMark: 100 },
    { metric: 'Coverage', fullMark: 100 },
    { metric: 'Duration', fullMark: 100 },
    { metric: 'Community', fullMark: 100 },
    { metric: 'Consistency', fullMark: 100 }
  ];

  // Add selected users data or default to top users
  const usersToShow =
    selectedUsers.length > 0 ? selectedUsers : topUsers.slice(0, 3);

  usersToShow.forEach((user) => {
    const userProfile = processUserEngagementProfile(user);
    userProfile.forEach((metric, index) => {
      radarData[index][user.name] = metric[user.name];
    });
  });

  // Color scheme for different users
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  const handleUserSelection = (e) => {
    const userId = e.target.value;
    if (userId === '') {
      setSelectedUsers([]);
      return;
    }

    const user = users.find((u) => u.uid === userId);
    if (user && !selectedUsers.find((u) => u.uid === userId)) {
      if (selectedUsers.length < 3) {
        setSelectedUsers([...selectedUsers, user]);
      } else {
        // Replace oldest selection
        setSelectedUsers([...selectedUsers.slice(1), user]);
      }
    }
  };

  const removeUser = (userToRemove) => {
    setSelectedUsers(selectedUsers.filter((u) => u.uid !== userToRemove.uid));
  };

  return (
    <Container>
      <Controls>
        <ControlsTitle>Compare Users (max 3)</ControlsTitle>
        <UserSelector onChange={handleUserSelection} value="">
          <option value="">Add user to compare...</option>
          {users
            .filter((user) => !usersToShow.find((u) => u.uid === user.uid))
            .map((user) => (
              <option key={user.uid} value={user.uid}>
                {user.name} (Score: {user.engagementScore || 0})
              </option>
            ))}
        </UserSelector>

        {selectedUsers.length > 0 && (
          <SelectedUsers>
            {selectedUsers.map((user, index) => (
              <UserTag key={user.uid} $color={colors[index]}>
                {user.name}
                <RemoveButton onClick={() => removeUser(user)}>×</RemoveButton>
              </UserTag>
            ))}
          </SelectedUsers>
        )}
      </Controls>

      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            data={radarData}
            margin={{
              top: isMobile ? 15 : 10,
              right: isMobile ? 30 : 40,
              bottom: 20,
              left: isMobile ? 30 : 40
            }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis
              dataKey="metric"
              tick={<CustomRadarTick />}
              className="radar-angle-axis"
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: 'var(--light-brown)', dx: 2, dy: -1 }}
              tickFormatter={(value) => `${value}%`}
              ticks={[0, 20, 40, 60, 80, 100]}
            />

            {usersToShow.map((user, index) => (
              <Radar
                key={user.uid}
                name={user.name}
                dataKey={user.name}
                stroke={colors[index]}
                fill={colors[index]}
                fillOpacity={0.1}
                strokeWidth={2}
                dot={{ r: 4, fill: colors[index] }}
              />
            ))}

            <Tooltip content={CustomTooltip} />
            {/* <Legend
              verticalAlign="top"
              height={isMobile ? 40 : 30}
              content={renderLegend}
            /> */}
          </RadarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      <MetricDescription>
        <DescriptionTitle>Understanding the Engagement Radar:</DescriptionTitle>
        <ScaleExplanation>
          <strong>Scale:</strong> 0% = Lowest performer in session, 100% =
          Highest performer in session. All metrics are relative to other users
          in this specific session.
        </ScaleExplanation>
        <MetricsList>
          <MetricItem>
            <strong>Quality:</strong> Content quality score based on comment
            length and depth
          </MetricItem>
          <MetricItem>
            <strong>Frequency:</strong> Total comments and replies relative to
            most active user
          </MetricItem>
          <MetricItem>
            <strong>Coverage:</strong> Percentage of available questions engaged
            with
          </MetricItem>
          <MetricItem>
            <strong>Duration:</strong> Session time relative to most active user
          </MetricItem>
          <MetricItem>
            <strong>Community:</strong> Reply-to-comment ratio (community
            interaction)
          </MetricItem>
          <MetricItem>
            <strong>Consistency:</strong> Overall engagement score (balanced
            participation)
          </MetricItem>
        </MetricsList>
      </MetricDescription>
    </Container>
  );
}

const Container = styled.div`
  padding: 1rem 0 0;
  width: 100%;
`;

const Controls = styled.div`
  margin-bottom: 1.625rem;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 1.5rem;
  }
`;

const ChartWrapper = styled.div`
  height: 400px;
  width: 100%;

  .recharts-legend-wrapper,
  .recharts-default-legend {
    position: static !important;
    text-align: center !important;
    display: block !important;
    width: 100% !important;
  }

  .recharts-default-legend {
    padding: 0 1rem !important;
  }

  .recharts-legend-item {
    margin-bottom: 0.25rem;
  }

  .recharts-legend-item:not(:last-child) {
    margin-right: 18px !important;
  }

  .recharts-legend-item:last-child {
    margin-right: 0 !important;
  }

  /* Minimal approach: Only target tooltips specifically at (0,0) coordinates */
  .recharts-tooltip-wrapper[style*='transform: translate(0px, 0px)'] {
    opacity: 0 !important;
    transition: opacity 0.2s ease !important;
  }

  /* Move chart surface up by 10px */
  .recharts-wrapper > .recharts-surface {
    transform: translateY(-25px) !important;
  }

  /* Ensure tooltips appear above transformed surface */
  .recharts-wrapper {
    position: relative;
    z-index: auto;
  }

  .recharts-tooltip-wrapper {
    position: absolute !important;
    transition: transform 0.2s ease !important;
    z-index: 1001 !important;
  }

  @media (max-width: 767px) {
    height: 420px;
  }

  @media (max-width: 480px) {
    height: 380px;
  }
`;

const ControlsTitle = styled.h4`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
`;

const UserSelector = styled.select`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  max-width: 300px;
  padding: 0.5rem;
  width: 100%;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }
`;

const SelectedUsers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const UserTag = styled.div`
  align-items: center;
  background: ${(props) => props.$color}20;
  border: 1px solid ${(props) => props.$color};
  border-radius: 20px;
  color: ${(props) => props.$color};
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;

  &:hover {
    background: ${(props) => props.$color}30;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 1rem;
  margin-left: 0.5rem;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  font-family: 'Inter', sans-serif;
  margin: 0;
  text-align: center;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TooltipValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  margin: 0.25rem 0;
`;

const TooltipNote = styled.div`
  border-top: 1px solid #e5e5e5;
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.7rem;
  font-style: italic;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
`;

const LegendText = styled.span`
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
`;

const MetricDescription = styled.div`
  background: #f9f9f9;
  border-radius: 6px;
  padding: 1rem;
`;

const DescriptionTitle = styled.h5`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
`;

const ScaleExplanation = styled.div`
  background: #e3f2fd;
  border-left: 4px solid var(--blue);
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  padding: 0.75rem;
`;

const MetricsList = styled.div`
  display: grid;
  gap: 0.25rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetricItem = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  line-height: 1.4;
`;

export default EngagementQualityRadarChart;
